import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/api';
import ScreenshotUpload from '../../components/ScreenshotUpload';
import {
  OcrMatchWarnings,
  StatsScoreCheck,
  collectOcrMatchIssues,
  hasOcrMatchIssues,
  sumStatPoints,
  scoreMismatches
} from '../../components/OcrMatchWarnings';

const PlayoffManagement = () => {
  const { t } = useTranslation();
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [bracket, setBracket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  
  // Score entry modal state
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [scoreData, setScoreData] = useState({
    higherSeedScore: '',
    lowerSeedScore: ''
  });
  
  // Stats entry state
  const [showStatsEntry, setShowStatsEntry] = useState(false);
  const [higherSeedPlayers, setHigherSeedPlayers] = useState([]);
  const [lowerSeedPlayers, setLowerSeedPlayers] = useState([]);
  const [higherSeedStats, setHigherSeedStats] = useState({});
  const [lowerSeedStats, setLowerSeedStats] = useState({});
  const [showBenched, setShowBenched] = useState(false); // Vom GM deaktivierte Spieler einblenden

  // Vom GM deaktivierte Spieler sind standardmäßig ausgeblendet — außer sie haben schon Minuten
  const visiblePlayers = (players, stats) => (showBenched
    ? players
    : players.filter(p => !p.isBenched || stats[p.id]?.minutes > 0));
  
  // OCR upload state
  const [entryMethod, setEntryMethod] = useState('manual'); // 'manual' or 'ocr'
  const [ocrData, setOcrData] = useState(null);
  const [allPlayers, setAllPlayers] = useState([]); // For OCR player matching
  const [ocrExtracted, setOcrExtracted] = useState(false); // Track if OCR was used
  const [originalHigherSeedPlayers, setOriginalHigherSeedPlayers] = useState([]); // Store full roster
  const [originalLowerSeedPlayers, setOriginalLowerSeedPlayers] = useState([]); // Store full roster
  const [ocrMatchIssues, setOcrMatchIssues] = useState(null); // Namenszuordnung: was der OCR-Abgleich nicht aufloesen konnte

  useEffect(() => {
    fetchSeasons();
  }, []);

  useEffect(() => {
    if (selectedSeason) {
      fetchBracket();
    }
  }, [selectedSeason]);

  const fetchSeasons = async () => {
    try {
      const response = await api.get('/seasons');
      setSeasons(response.data);
      
      // Auto-select the active season
      const activeSeason = response.data.find(s => s.isActive);
      if (activeSeason && !selectedSeason) {
        setSelectedSeason(activeSeason.id.toString());
      }
    } catch (error) {
      setError('Failed to load seasons');
      console.error(error);
    }
  };

  const fetchBracket = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/playoffs/bracket/${selectedSeason}`);
      setBracket(response.data);
    } catch (error) {
      console.error('Error fetching bracket:', error);
      setBracket(null);
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    if (!selectedSeason) {
      setError('Please select a season');
      return;
    }

    const season = seasons.find(s => s.id.toString() === selectedSeason.toString());
    const formatLabels = {
      'top16': 'Top 16 bracket (1v16, 2v15, …)',
      'top8': 'Top 8 bracket (1v8, 2v7, …)',
      'top10_playin': 'Top 10 with Play-In tournament',
      'conference': 'Conference-based bracket',
      'custom': 'Custom playoff format'
    };
    const formatDesc = formatLabels[season?.playoffFormat] || season?.playoffFormat || 'playoffs';
    if (!window.confirm(`Initialize playoffs for this season?\nFormat: ${formatDesc}`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');
      
      const response = await api.post(`/playoffs/initialize/${selectedSeason}`);
      setMessage(response.data.message);
      
      // Refresh bracket
      await fetchBracket();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to initialize playoffs');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvancePlayIn = async () => {
    if (!window.confirm('Advance Play-In to First Round? Make sure all Play-In games are complete.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');
      
      const response = await api.post(`/playoffs/advance-play-in/${selectedSeason}`);
      setMessage(response.data.message);
      
      // Refresh bracket
      await fetchBracket();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to advance Play-In');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvanceRound = async () => {
    if (!window.confirm('Advance to next round? Make sure all series in the current round are complete.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');
      
      const response = await api.post(`/playoffs/advance-round/${selectedSeason}`);
      setMessage(response.data.message);
      
      // Refresh bracket
      await fetchBracket();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to advance round');
    } finally {
      setLoading(false);
    }
  };

  const handleFinishSeason = async () => {
    if (!window.confirm('Finish this season? This will declare the Finals winner as Champion and mark the season as completed.')) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      setMessage('');
      const response = await api.post(`/playoffs/finish-season/${selectedSeason}`);
      setMessage(response.data.message);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to finish season');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGame3 = async () => {
    if (!window.confirm('Create Play-In Game 3? Make sure Games 1 and 2 (7v8 and 9v10) are complete.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');
      
      const response = await api.post(`/playoffs/create-play-in-game3/${selectedSeason}`);
      setMessage(response.data.message);
      
      // Refresh bracket
      await fetchBracket();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create Game 3');
    } finally {
      setLoading(false);
    }
  };

  const openScoreModal = async (series) => {
    setSelectedSeries(series);
    
    // Check if there's a recent game in this series that was played
    const latestGame = series.games && series.games.length > 0 
      ? series.games.sort((a, b) => b.id - a.id)[0] 
      : null;
    
    // If editing an existing game, load its data
    if (latestGame && latestGame.played) {
      setScoreData({
        higherSeedScore: latestGame.homeTeamId === series.higherSeedId ? latestGame.homeScore : latestGame.awayScore,
        lowerSeedScore: latestGame.homeTeamId === series.lowerSeedId ? latestGame.homeScore : latestGame.awayScore,
        gameId: latestGame.id // Store game ID for editing
      });
      
      // Load existing stats if available
      try {
        const gameResponse = await api.get(`/games/${latestGame.id}`);
        if (gameResponse.data.stats && gameResponse.data.stats.length > 0) {
          // Load players first
          const [higherResponse, lowerResponse] = await Promise.all([
            api.get(`/players/team/${series.higherSeedId}?includeBenched=true`),
            api.get(`/players/team/${series.lowerSeedId}?includeBenched=true`)
          ]);
          
          setHigherSeedPlayers(higherResponse.data);
          setLowerSeedPlayers(lowerResponse.data);
          
          // Map existing stats to players
          const higherStats = {};
          const lowerStats = {};
          
          higherResponse.data.forEach(player => {
            const existingStat = gameResponse.data.stats.find(s => s.playerId === player.id);
            higherStats[player.id] = existingStat || createEmptyStats(player.id, series.higherSeedId);
          });
          
          lowerResponse.data.forEach(player => {
            const existingStat = gameResponse.data.stats.find(s => s.playerId === player.id);
            lowerStats[player.id] = existingStat || createEmptyStats(player.id, series.lowerSeedId);
          });
          
          setHigherSeedStats(higherStats);
          setLowerSeedStats(lowerStats);
          setShowStatsEntry(true); // Auto-show stats if editing
        }
      } catch (error) {
        console.error('Error loading game stats:', error);
      }
    } else {
      setScoreData({
        higherSeedScore: '',
        lowerSeedScore: '',
        gameId: null
      });
      setShowStatsEntry(false);
    }
    
    setShowBenched(false);
    setOcrMatchIssues(null);
    setShowScoreModal(true);
  };

  const toggleStatsEntry = async () => {
    if (!showStatsEntry && selectedSeries) {
      // Load players for both teams
      try {
        const [higherResponse, lowerResponse, allPlayersResponse] = await Promise.all([
          api.get(`/players/team/${selectedSeries.higherSeedId}?includeBenched=true`),
          api.get(`/players/team/${selectedSeries.lowerSeedId}?includeBenched=true`),
          api.get('/players?includeBenched=true') // Get all players for OCR matching
        ]);
        
        setHigherSeedPlayers(higherResponse.data);
        setLowerSeedPlayers(lowerResponse.data);
        setAllPlayers(allPlayersResponse.data);
        
        // Store original full rosters for manual entry mode
        setOriginalHigherSeedPlayers(higherResponse.data);
        setOriginalLowerSeedPlayers(lowerResponse.data);
        
        // Initialize stats
        const higherStats = {};
        const lowerStats = {};
        
        higherResponse.data.forEach(player => {
          higherStats[player.id] = createEmptyStats(player.id, selectedSeries.higherSeedId);
        });
        
        lowerResponse.data.forEach(player => {
          lowerStats[player.id] = createEmptyStats(player.id, selectedSeries.lowerSeedId);
        });
        
        setHigherSeedStats(higherStats);
        setLowerSeedStats(lowerStats);
      } catch (error) {
        console.error('Error loading players:', error);
        alert('Failed to load players');
        return;
      }
    }
    setShowStatsEntry(!showStatsEntry);
  };

  const handleOcrExtractComplete = (data) => {
    setOcrData(data);
    setOcrExtracted(true); // Mark that OCR was used

    const issues = [
      collectOcrMatchIssues({
        ocrPlayers: data.home.players,
        roster: originalHigherSeedPlayers,
        teamName: selectedSeries.higherSeed?.name,
        allPlayers
      }),
      collectOcrMatchIssues({
        ocrPlayers: data.away.players,
        roster: originalLowerSeedPlayers,
        teamName: selectedSeries.lowerSeed?.name,
        allPlayers
      })
    ];
    setOcrMatchIssues(hasOcrMatchIssues(issues) ? issues : null);

    // Convert OCR data to stats format
    const higherStats = {};
    const lowerStats = {};

    // Nur Treffer uebernehmen, die auch im Kader der jeweiligen Mannschaft stehen.
    // Ein kaderfremder Treffer waere in der Tabelle unten unsichtbar, ginge beim
    // Absenden aber trotzdem mit — der Hinweiskasten listet ihn stattdessen auf.
    const higherRosterIds = new Set(originalHigherSeedPlayers.map(p => p.id));
    const lowerRosterIds = new Set(originalLowerSeedPlayers.map(p => p.id));

    const extractedHigherPlayerIds = data.home.players
      .filter(p => p.playerId && higherRosterIds.has(p.playerId))
      .map(p => p.playerId);

    const extractedLowerPlayerIds = data.away.players
      .filter(p => p.playerId && lowerRosterIds.has(p.playerId))
      .map(p => p.playerId);
    
    // Update player lists to only show extracted players
    setHigherSeedPlayers(prev => prev.filter(p => extractedHigherPlayerIds.includes(p.id)));
    setLowerSeedPlayers(prev => prev.filter(p => extractedLowerPlayerIds.includes(p.id)));
    
    // Process home team (higher seed)
    data.home.players.forEach(ocrPlayer => {
      if (ocrPlayer.playerId && higherRosterIds.has(ocrPlayer.playerId)) {
        higherStats[ocrPlayer.playerId] = {
          playerId: ocrPlayer.playerId,
          teamId: selectedSeries.higherSeedId,
          ...ocrPlayer.stats
        };
      }
    });
    
    // Process away team (lower seed)
    data.away.players.forEach(ocrPlayer => {
      if (ocrPlayer.playerId && lowerRosterIds.has(ocrPlayer.playerId)) {
        lowerStats[ocrPlayer.playerId] = {
          playerId: ocrPlayer.playerId,
          teamId: selectedSeries.lowerSeedId,
          ...ocrPlayer.stats
        };
      }
    });
    
    setHigherSeedStats(higherStats);
    setLowerSeedStats(lowerStats);
    
    // Auto-switch to review mode
    setEntryMethod('review');
  };

  const handleEntryMethodChange = (method) => {
    setEntryMethod(method);
    
    // If switching to manual mode after OCR extraction, restore full player lists
    if (method === 'manual' && ocrExtracted) {
      setHigherSeedPlayers(originalHigherSeedPlayers);
      setLowerSeedPlayers(originalLowerSeedPlayers);
      
      // Re-initialize stats for all players
      const higherStats = {};
      const lowerStats = {};

      originalHigherSeedPlayers.forEach(player => {
        higherStats[player.id] = higherSeedStats[player.id] || createEmptyStats(player.id, selectedSeries.higherSeedId);
      });

      originalLowerSeedPlayers.forEach(player => {
        lowerStats[player.id] = lowerSeedStats[player.id] || createEmptyStats(player.id, selectedSeries.lowerSeedId);
      });

      setHigherSeedStats(higherStats);
      setLowerSeedStats(lowerStats);
    }
  };

  const createEmptyStats = (playerId, teamId) => ({
    playerId,
    teamId,
    minutes: 0,
    points: 0,
    rebounds: 0,
    assists: 0,
    steals: 0,
    blocks: 0,
    turnovers: 0,
    fieldGoalsMade: 0,
    fieldGoalsAttempted: 0,
    threePointersMade: 0,
    threePointersAttempted: 0,
    freeThrowsMade: 0,
    freeThrowsAttempted: 0,
    offensiveRebounds: 0,
    fouls: 0
  });

  const updateStat = (playerId, isHigherSeed, stat, value) => {
    const numValue = parseInt(value) || 0;
    if (isHigherSeed) {
      setHigherSeedStats(prev => ({
        ...prev,
        [playerId]: {
          ...prev[playerId],
          [stat]: numValue
        }
      }));
    } else {
      setLowerSeedStats(prev => ({
        ...prev,
        [playerId]: {
          ...prev[playerId],
          [stat]: numValue
        }
      }));
    }
  };

  const removePlayer = (playerId, isHigherSeed) => {
    if (isHigherSeed) {
      setHigherSeedPlayers(prev => prev.filter(p => p.id !== playerId));
      setHigherSeedStats(prev => {
        const updated = { ...prev };
        delete updated[playerId];
        return updated;
      });
    } else {
      setLowerSeedPlayers(prev => prev.filter(p => p.id !== playerId));
      setLowerSeedStats(prev => {
        const updated = { ...prev };
        delete updated[playerId];
        return updated;
      });
    }
  };

  const handleSubmitScore = async (e) => {
    e.preventDefault();
    
    const higherScore = parseInt(scoreData.higherSeedScore);
    const lowerScore = parseInt(scoreData.lowerSeedScore);

    if (!higherScore || !lowerScore || higherScore < 0 || lowerScore < 0) {
      alert('Please enter valid scores');
      return;
    }

    // Punktprobe: der rote Kasten in der Tabelle ist nur ein Hinweis. Fehlt
    // eine Zeile aus dem Screenshot, soll das Absenden bestaetigt werden.
    if (showStatsEntry) {
      const rows = [
        { name: selectedSeries?.higherSeed?.name, sum: sumStatPoints(higherSeedStats), score: higherScore },
        { name: selectedSeries?.lowerSeed?.name, sum: sumStatPoints(lowerSeedStats), score: lowerScore }
      ];
      const mismatches = scoreMismatches(rows);
      if (mismatches.length > 0 && rows.every(r => r.sum === 0)) {
        if (!window.confirm(t('ocrReview.confirmNoStats'))) return;
      } else if (mismatches.length > 0) {
        const details = mismatches
          .map(r => t('ocrReview.confirmMismatchLine', {
            team: r.name, sum: r.sum, score: r.score, diff: r.score - r.sum
          }))
          .join('\n');
        if (!window.confirm(t('ocrReview.confirmMismatch', { details }))) return;
      }
    }

    try {
      setLoading(true);
      
      // Collect all stats
      const allStats = showStatsEntry
        ? [...Object.values(higherSeedStats).filter(s => s.minutes > 0),
           ...Object.values(lowerSeedStats).filter(s => s.minutes > 0)]
        : [];

      if (scoreData.gameId) {
        // Editing an existing game — update in place
        const gameResponse = await api.put(`/playoffs/games/${scoreData.gameId}/with-stats`, {
          higherSeedScore: higherScore,
          lowerSeedScore: lowerScore,
          stats: allStats
        });
        setMessage(gameResponse.data.message);
      } else if (showStatsEntry) {
        // New game with stats
        const gameResponse = await api.post('/playoffs/series/' + selectedSeries.id + '/with-stats', {
          higherSeedScore: higherScore,
          lowerSeedScore: lowerScore,
          stats: allStats
        });
        setMessage(gameResponse.data.message);
      } else {
        // New game without stats
        const response = await api.put(`/playoffs/series/${selectedSeries.id}`, {
          higherSeedScore: higherScore,
          lowerSeedScore: lowerScore
        });
        setMessage(response.data.message);
      }
      
      setShowScoreModal(false);
      
      // Refresh bracket
      await fetchBracket();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to submit score');
    } finally {
      setLoading(false);
    }
  };

  const isCustomFormat = bracket && bracket.format === 'custom';
  const hasPlayInSeries = !isCustomFormat && bracket && (bracket['Play-In']?.Eastern?.length > 0 || bracket['Play-In']?.Western?.length > 0);
  const hasFirstRound = !isCustomFormat && bracket && (bracket['First Round']?.Eastern?.length > 0 || bracket['First Round']?.Western?.length > 0);
  const hasAnySeries = isCustomFormat 
    ? bracket && bracket.rounds && Object.values(bracket.rounds).some(r => r.length > 0)
    : hasPlayInSeries || hasFirstRound;

  // For custom format: determine if we can advance to next round
  const getCustomAdvanceInfo = () => {
    if (!isCustomFormat || !bracket?.config?.rounds) return { canAdvance: false };
    const config = bracket.config;
    const roundNames = config.rounds.map(r => r.name);
    
    // Find the latest round with series
    let currentRoundIndex = -1;
    for (let i = roundNames.length - 1; i >= 0; i--) {
      if (bracket.rounds[roundNames[i]] && bracket.rounds[roundNames[i]].length > 0) {
        currentRoundIndex = i;
        break;
      }
    }

    if (currentRoundIndex === -1 || currentRoundIndex >= roundNames.length - 1) {
      return { canAdvance: false, currentRound: roundNames[currentRoundIndex] };
    }

    const currentRoundSeries = bracket.rounds[roundNames[currentRoundIndex]] || [];
    const allComplete = currentRoundSeries.length > 0 && currentRoundSeries.every(s => s.isComplete);
    const nextRound = config.rounds[currentRoundIndex + 1];

    return {
      canAdvance: allComplete,
      currentRound: roundNames[currentRoundIndex],
      nextRound: nextRound?.name,
      allComplete,
      seriesCount: currentRoundSeries.length,
      completeCount: currentRoundSeries.filter(s => s.isComplete).length
    };
  };

  const renderSeries = (series) => {
    if (!series) return null;

    // Count games played in this series
    const gamesPlayed = series.games ? series.games.filter(g => g.played).length : 0;
    const totalGames = series.games ? series.games.length : 0;

    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm mb-2 border-l-4 border-blue-500">
        <div className="flex justify-between items-center">
          <div className="flex-1">
            <div className="font-medium">
              {series.higherSeed.city} {series.higherSeed.name}
              <span className="ml-2 text-sm font-bold text-blue-600 dark:text-blue-400">({series.higherSeedWins || 0})</span>
            </div>
            <div className="font-medium mt-1">
              {series.lowerSeed.city} {series.lowerSeed.name}
              <span className="ml-2 text-sm font-bold text-blue-600 dark:text-blue-400">({series.lowerSeedWins || 0})</span>
            </div>
            {series.seriesFormat && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {series.seriesFormat} • {gamesPlayed}/{totalGames} games played
              </div>
            )}
            {/* Show individual game results */}
            {series.games && series.games.length > 0 && (
              <div className="mt-2 text-xs space-y-1">
                {series.games.map((game, idx) => (
                  <div key={game.id} className="flex items-center gap-2">
                    <span className="text-gray-500 dark:text-gray-400">Game {idx + 1}:</span>
                    {game.played ? (
                      <span className="text-gray-700 dark:text-gray-300">
                        {game.homeTeam.abbreviation} {game.homeScore} - {game.awayScore} {game.awayTeam.abbreviation}
                        <span className="ml-1 text-green-600 dark:text-green-400">✓</span>
                      </span>
                    ) : (
                      <span className="text-gray-400">Not played</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="text-right flex gap-2 items-center">
            {!series.isComplete && (
              <button
                onClick={() => openScoreModal(series)}
                className="px-3 py-1 bg-blue-50 dark:bg-blue-900/200 text-white text-sm rounded hover:bg-blue-600"
              >
                📊 Enter Game Result
              </button>
            )}
            {series.games && series.games.some(g => g.played) && (
              <button
                onClick={() => openScoreModal(series)}
                className="px-3 py-1 bg-purple-500 text-white text-sm rounded hover:bg-purple-600"
              >
                ✏️ Edit Last Game
              </button>
            )}
            {series.isComplete ? (
              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 text-xs rounded">
                ✓ Complete
              </span>
            ) : (
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 text-xs rounded">
                In Progress
              </span>
            )}
          </div>
        </div>
        {series.winner && (
          <div className="mt-2 text-sm text-green-600 dark:text-green-400 font-medium">
            Winner: {series.winner.city} {series.winner.name}
          </div>
        )}
      </div>
    );
  };

  const renderConference = (conferenceSeries, title) => {
    if (!conferenceSeries || conferenceSeries.length === 0) return null;

    return (
      <div className="mb-4">
        <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2">{title}</h4>
        {conferenceSeries.map(series => (
          <div key={series.id}>
            {renderSeries(series)}
          </div>
        ))}
      </div>
    );
  };

  const renderRound = (roundName, roundData) => {
    if (!roundData) return null;

    const hasData = roundName === 'Finals' 
      ? roundData.length > 0
      : (roundData.Eastern?.length > 0 || roundData.Western?.length > 0);

    if (!hasData) return null;

    return (
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mb-4">
        <h3 className="text-lg font-bold mb-3">{roundName}</h3>
        
        {roundName === 'Finals' ? (
          roundData.map(series => (
            <div key={series.id}>
              {renderSeries(series)}
            </div>
          ))
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              {renderConference(roundData.Eastern, 'Eastern Conference')}
            </div>
            <div>
              {renderConference(roundData.Western, 'Western Conference')}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Playoff Management</h1>

      {/* Season Selection */}
      <div className="bg-white dark:bg-gray-800 shadow-md dark:shadow-gray-900 rounded-lg p-6 mb-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Season
          </label>
          <select
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
            className="w-full md:w-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
          >
            <option value="">-- Choose a season --</option>
            {seasons.map(season => (
              <option key={season.id} value={season.id}>
                {season.name}
              </option>
            ))}
          </select>
        </div>

        {/* Action Buttons */}
        {selectedSeason && (
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={handleInitialize}
              disabled={loading || hasAnySeries}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {hasAnySeries ? 'Playoffs Already Initialized' : 'Initialize Playoffs'}
            </button>

            {hasPlayInSeries && !hasFirstRound && (
              <>
                <button
                  onClick={handleCreateGame3}
                  disabled={loading}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400"
                >
                  Create Play-In Game 3
                </button>
                
                <button
                  onClick={handleAdvancePlayIn}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                >
                  Advance Play-In to First Round
                </button>
              </>
            )}

            {/* Finish Season button — shown when a bracket exists */}
            {hasAnySeries && (
              <button
                onClick={handleFinishSeason}
                disabled={loading}
                className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
              >
                🏆 Finish Season
              </button>
            )}

            {/* Custom format: Advance to Next Round button */}
            {isCustomFormat && (() => {
              const advanceInfo = getCustomAdvanceInfo();
              if (!advanceInfo.nextRound) return null;
              return (
                <button
                  onClick={handleAdvanceRound}
                  disabled={loading || !advanceInfo.canAdvance}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  title={!advanceInfo.canAdvance ? `${advanceInfo.completeCount}/${advanceInfo.seriesCount} series complete in "${advanceInfo.currentRound}"` : ''}
                >
                  Advance to {advanceInfo.nextRound}
                  {!advanceInfo.canAdvance && advanceInfo.seriesCount > 0 && (
                    <span className="ml-1 text-xs">({advanceInfo.completeCount}/{advanceInfo.seriesCount})</span>
                  )}
                </button>
              );
            })()}
          </div>
        )}

        {/* Messages */}
        {error && (
          <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
            {error}
          </div>
        )}
        {message && (
          <div className="mt-4 p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
            {message}
          </div>
        )}
      </div>

      {/* Bracket Display */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading bracket...</p>
        </div>
      )}

      {!loading && bracket && selectedSeason && (
        <div className="bg-white dark:bg-gray-800 shadow-md dark:shadow-gray-900 rounded-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Playoff Bracket</h2>
          
          {!hasAnySeries && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <p>No playoff data yet. Click "Initialize Playoffs" to begin.</p>
            </div>
          )}

          {/* Custom format bracket display */}
          {isCustomFormat && bracket.config?.rounds && (
            <>
              {bracket.config.rounds.map((roundConfig) => {
                const roundSeries = bracket.rounds[roundConfig.name] || [];
                if (roundSeries.length === 0) return null;
                return (
                  <div key={roundConfig.name} className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold">{roundConfig.name}</h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{roundConfig.seriesFormat}</span>
                    </div>
                    {bracket.config.conferenceDependent ? (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          {renderConference(roundSeries.filter(s => s.conference === 'Eastern'), 'Eastern Conference')}
                        </div>
                        <div>
                          {renderConference(roundSeries.filter(s => s.conference === 'Western'), 'Western Conference')}
                        </div>
                      </div>
                    ) : (
                      roundSeries
                        .sort((a, b) => a.seriesNumber - b.seriesNumber)
                        .map(series => (
                          <div key={series.id}>{renderSeries(series)}</div>
                        ))
                    )}
                  </div>
                );
              })}
              
              {/* Show champion if final round is complete */}
              {(() => {
                const lastRound = bracket.config.rounds[bracket.config.rounds.length - 1];
                const finalSeries = bracket.rounds[lastRound.name] || [];
                const champion = finalSeries.length === 1 && finalSeries[0].isComplete && finalSeries[0].winner;
                if (!champion) return null;
                return (
                  <div className="mt-4 p-6 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg shadow-xl text-center">
                    <div className="text-white">
                      <div className="text-2xl font-bold mb-2">🏆 CHAMPIONS 🏆</div>
                      <div className="text-3xl font-bold">{champion.city} {champion.name}</div>
                    </div>
                  </div>
                );
              })()}
            </>
          )}

          {/* Standard format bracket display */}
          {!isCustomFormat && (
            <>
              {renderRound('Play-In', bracket['Play-In'])}
              {renderRound('First Round', bracket['First Round'])}
              {renderRound('Conference Semifinals', bracket['Conference Semifinals'])}
              {renderRound('Conference Finals', bracket['Conference Finals'])}
              {renderRound('Finals', bracket.Finals)}
            </>
          )}
        </div>
      )}

      {/* Score Entry Modal */}
      {showScoreModal && selectedSeries && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">
              {scoreData.gameId ? '✏️ Edit Game Result' : '📊 Enter Game Result'}
            </h3>
            {scoreData.gameId && (
              <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800">
                <p className="text-sm">
                  <strong>Note:</strong> You are editing an existing game. Changes will update the stats and series record.
                </p>
              </div>
            )}
            
            <form onSubmit={handleSubmitScore}>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {selectedSeries.higherSeed.city} {selectedSeries.higherSeed.name} Score
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      (Currently {selectedSeries.higherSeedWins || 0} wins)
                    </span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={scoreData.higherSeedScore}
                    onChange={(e) => setScoreData({...scoreData, higherSeedScore: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder="Enter score"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {selectedSeries.lowerSeed.city} {selectedSeries.lowerSeed.name} Score
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      (Currently {selectedSeries.lowerSeedWins || 0} wins)
                    </span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={scoreData.lowerSeedScore}
                    onChange={(e) => setScoreData({...scoreData, lowerSeedScore: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder="Enter score"
                    required
                  />
                </div>
              </div>

              <div className="text-xs text-gray-600 dark:text-gray-400 mb-4 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                💡 Series Format: {selectedSeries.seriesFormat || 'Best-of-7'} 
                {selectedSeries.seriesFormat === 'Best-of-1' && ' (Winner advances immediately)'}
                {selectedSeries.seriesFormat === 'Best-of-3' && ' (First to 2 wins)'}
                {selectedSeries.seriesFormat === 'Best-of-5' && ' (First to 3 wins)'}
                {selectedSeries.seriesFormat === 'Best-of-7' && ' (First to 4 wins)'}
              </div>

              {/* Stats Entry Toggle */}
              <div className="mb-4">
                <button
                  type="button"
                  onClick={toggleStatsEntry}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  {showStatsEntry ? '📊 Hide Stats Entry' : '📊 Add Player Stats'}
                </button>
              </div>

              {/* Entry Method Tabs */}
              {showStatsEntry && (
                <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex space-x-1">
                    <button
                      type="button"
                      onClick={() => handleEntryMethodChange('manual')}
                      className={`px-4 py-2 font-medium rounded-t-lg ${
                        entryMethod === 'manual' || entryMethod === 'review'
                          ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-b-2 border-blue-600'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      ✏️ Manual Entry
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEntryMethodChange('ocr')}
                      className={`px-4 py-2 font-medium rounded-t-lg ${
                        entryMethod === 'ocr'
                          ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-b-2 border-blue-600'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      📸 Upload Screenshots
                    </button>
                  </div>
                </div>
              )}

              {/* OCR Upload Section */}
              {showStatsEntry && entryMethod === 'ocr' && (
                <ScreenshotUpload
                  homeTeam={selectedSeries.higherSeed}
                  awayTeam={selectedSeries.lowerSeed}
                  players={allPlayers}
                  homePlayers={originalHigherSeedPlayers}
                  awayPlayers={originalLowerSeedPlayers}
                  onExtractComplete={handleOcrExtractComplete}
                />
              )}

              {/* Stats Entry Section */}
              {showStatsEntry && (entryMethod === 'manual' || entryMethod === 'review') && (
                <div className="mb-4 space-y-4">
                  {/* Namensabgleich: was der OCR-Abgleich nicht aufloesen konnte */}
                  {(entryMethod === 'review' || (entryMethod === 'manual' && ocrExtracted)) && (
                    <OcrMatchWarnings issues={ocrMatchIssues} />
                  )}

                  {/* Punktprobe: Summe der Statzeilen gegen den eingetragenen Endstand */}
                  <StatsScoreCheck rows={[
                    { name: selectedSeries.higherSeed?.name, sum: sumStatPoints(higherSeedStats), score: parseInt(scoreData.higherSeedScore, 10) },
                    { name: selectedSeries.lowerSeed?.name, sum: sumStatPoints(lowerSeedStats), score: parseInt(scoreData.lowerSeedScore, 10) }
                  ]} />

                  {/* Review Mode Banner */}
                  {(entryMethod === 'review' || (entryMethod === 'manual' && ocrExtracted)) && ocrData && (
                    <div className="p-4 bg-green-50 border-l-4 border-green-400 dark:border-green-700 mb-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-green-800">
                            Stats extracted successfully!
                          </h3>
                          <div className="mt-2 text-sm text-green-700 dark:text-green-400">
                            <p>Review the extracted stats below and make any necessary corrections before submitting. Only players found in the screenshots are shown.</p>
                            {ocrData.warnings && (ocrData.warnings.home.length > 0 || ocrData.warnings.away.length > 0) && (
                              <div className="mt-2">
                                <p className="font-semibold">⚠️ Warnings:</p>
                                {ocrData.warnings.home.map((w, idx) => (
                                  <p key={`home-${idx}`} className="text-xs">• {w.player.playerName}: {w.warnings.join(', ')}</p>
                                ))}
                                {ocrData.warnings.away.map((w, idx) => (
                                  <p key={`away-${idx}`} className="text-xs">• {w.player.playerName}: {w.warnings.join(', ')}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {[...higherSeedPlayers, ...lowerSeedPlayers].some(p => p.isBenched) && (
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={showBenched}
                        onChange={(e) => setShowBenched(e.target.checked)}
                      />
                      {t('bench.showBenched')}
                      <span className="text-gray-500 dark:text-gray-400">
                        ({[...higherSeedPlayers, ...lowerSeedPlayers].filter(p => p.isBenched).length})
                      </span>
                    </label>
                  )}

                  {/* Higher Seed Team Stats */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-bold mb-2">{selectedSeries.higherSeed.city} {selectedSeries.higherSeed.name} Stats</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-gray-700">
                          <tr>
                            <th className="px-2 py-1 text-left">Player</th>
                            <th className="px-2 py-1">MIN</th>
                            <th className="px-2 py-1">PTS</th>
                            <th className="px-2 py-1">REB</th>
                            <th className="px-2 py-1">AST</th>
                            <th className="px-2 py-1">STL</th>
                            <th className="px-2 py-1">BLK</th>
                            <th className="px-2 py-1">TO</th>
                            <th className="px-2 py-1">FG</th>
                            <th className="px-2 py-1">3PT</th>
                            <th className="px-2 py-1">FT</th>
                            <th className="px-2 py-1">OR</th>
                            <th className="px-2 py-1">FLS</th>
                            <th className="px-2 py-1"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {visiblePlayers(higherSeedPlayers, higherSeedStats).map(player => (
                            <tr key={player.id} className={`border-t ${player.isBenched ? 'opacity-60' : ''}`}>
                              <td className="px-2 py-1 font-medium">
                                {player.firstName} {player.lastName}
                                {player.isBenched && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({t('bench.benchedHint')})</span>}
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" max="48" value={higherSeedStats[player.id]?.minutes || 0} 
                                  onChange={(e) => updateStat(player.id, true, 'minutes', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={higherSeedStats[player.id]?.points || 0}
                                  onChange={(e) => updateStat(player.id, true, 'points', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={higherSeedStats[player.id]?.rebounds || 0}
                                  onChange={(e) => updateStat(player.id, true, 'rebounds', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={higherSeedStats[player.id]?.assists || 0}
                                  onChange={(e) => updateStat(player.id, true, 'assists', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={higherSeedStats[player.id]?.steals || 0}
                                  onChange={(e) => updateStat(player.id, true, 'steals', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={higherSeedStats[player.id]?.blocks || 0}
                                  onChange={(e) => updateStat(player.id, true, 'blocks', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={higherSeedStats[player.id]?.turnovers || 0}
                                  onChange={(e) => updateStat(player.id, true, 'turnovers', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <div className="flex gap-1">
                                  <input type="number" min="0" value={higherSeedStats[player.id]?.fieldGoalsMade || 0}
                                    onChange={(e) => updateStat(player.id, true, 'fieldGoalsMade', e.target.value)}
                                    className="w-12 px-1 py-1 border rounded text-center" placeholder="M" />
                                  <span>/</span>
                                  <input type="number" min="0" value={higherSeedStats[player.id]?.fieldGoalsAttempted || 0}
                                    onChange={(e) => updateStat(player.id, true, 'fieldGoalsAttempted', e.target.value)}
                                    className="w-12 px-1 py-1 border rounded text-center" placeholder="A" />
                                </div>
                              </td>
                              <td className="px-2 py-1">
                                <div className="flex gap-1">
                                  <input type="number" min="0" value={higherSeedStats[player.id]?.threePointersMade || 0}
                                    onChange={(e) => updateStat(player.id, true, 'threePointersMade', e.target.value)}
                                    className="w-12 px-1 py-1 border rounded text-center" placeholder="M" />
                                  <span>/</span>
                                  <input type="number" min="0" value={higherSeedStats[player.id]?.threePointersAttempted || 0}
                                    onChange={(e) => updateStat(player.id, true, 'threePointersAttempted', e.target.value)}
                                    className="w-12 px-1 py-1 border rounded text-center" placeholder="A" />
                                </div>
                              </td>
                              <td className="px-2 py-1">
                                <div className="flex gap-1">
                                  <input type="number" min="0" value={higherSeedStats[player.id]?.freeThrowsMade || 0}
                                    onChange={(e) => updateStat(player.id, true, 'freeThrowsMade', e.target.value)}
                                    className="w-12 px-1 py-1 border rounded text-center" placeholder="M" />
                                  <span>/</span>
                                  <input type="number" min="0" value={higherSeedStats[player.id]?.freeThrowsAttempted || 0}
                                    onChange={(e) => updateStat(player.id, true, 'freeThrowsAttempted', e.target.value)}
                                    className="w-12 px-1 py-1 border rounded text-center" placeholder="A" />
                                </div>
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={higherSeedStats[player.id]?.offensiveRebounds || 0}
                                  onChange={(e) => updateStat(player.id, true, 'offensiveRebounds', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={higherSeedStats[player.id]?.fouls || 0}
                                  onChange={(e) => updateStat(player.id, true, 'fouls', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <button
                                  type="button"
                                  onClick={() => removePlayer(player.id, true)}
                                  className="text-red-500 hover:text-red-700 dark:text-red-400 text-lg font-bold"
                                  title={`Remove ${player.firstName} ${player.lastName}`}
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Lower Seed Team Stats */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-bold mb-2">{selectedSeries.lowerSeed.city} {selectedSeries.lowerSeed.name} Stats</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-gray-700">
                          <tr>
                            <th className="px-2 py-1 text-left">Player</th>
                            <th className="px-2 py-1">MIN</th>
                            <th className="px-2 py-1">PTS</th>
                            <th className="px-2 py-1">REB</th>
                            <th className="px-2 py-1">AST</th>
                            <th className="px-2 py-1">STL</th>
                            <th className="px-2 py-1">BLK</th>
                            <th className="px-2 py-1">TO</th>
                            <th className="px-2 py-1">FG</th>
                            <th className="px-2 py-1">3PT</th>
                            <th className="px-2 py-1">FT</th>
                            <th className="px-2 py-1">OR</th>
                            <th className="px-2 py-1">FLS</th>
                            <th className="px-2 py-1"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {visiblePlayers(lowerSeedPlayers, lowerSeedStats).map(player => (
                            <tr key={player.id} className={`border-t ${player.isBenched ? 'opacity-60' : ''}`}>
                              <td className="px-2 py-1 font-medium">
                                {player.firstName} {player.lastName}
                                {player.isBenched && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({t('bench.benchedHint')})</span>}
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" max="48" value={lowerSeedStats[player.id]?.minutes || 0}
                                  onChange={(e) => updateStat(player.id, false, 'minutes', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={lowerSeedStats[player.id]?.points || 0}
                                  onChange={(e) => updateStat(player.id, false, 'points', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={lowerSeedStats[player.id]?.rebounds || 0}
                                  onChange={(e) => updateStat(player.id, false, 'rebounds', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={lowerSeedStats[player.id]?.assists || 0}
                                  onChange={(e) => updateStat(player.id, false, 'assists', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={lowerSeedStats[player.id]?.steals || 0}
                                  onChange={(e) => updateStat(player.id, false, 'steals', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={lowerSeedStats[player.id]?.blocks || 0}
                                  onChange={(e) => updateStat(player.id, false, 'blocks', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={lowerSeedStats[player.id]?.turnovers || 0}
                                  onChange={(e) => updateStat(player.id, false, 'turnovers', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <div className="flex gap-1">
                                  <input type="number" min="0" value={lowerSeedStats[player.id]?.fieldGoalsMade || 0}
                                    onChange={(e) => updateStat(player.id, false, 'fieldGoalsMade', e.target.value)}
                                    className="w-12 px-1 py-1 border rounded text-center" placeholder="M" />
                                  <span>/</span>
                                  <input type="number" min="0" value={lowerSeedStats[player.id]?.fieldGoalsAttempted || 0}
                                    onChange={(e) => updateStat(player.id, false, 'fieldGoalsAttempted', e.target.value)}
                                    className="w-12 px-1 py-1 border rounded text-center" placeholder="A" />
                                </div>
                              </td>
                              <td className="px-2 py-1">
                                <div className="flex gap-1">
                                  <input type="number" min="0" value={lowerSeedStats[player.id]?.threePointersMade || 0}
                                    onChange={(e) => updateStat(player.id, false, 'threePointersMade', e.target.value)}
                                    className="w-12 px-1 py-1 border rounded text-center" placeholder="M" />
                                  <span>/</span>
                                  <input type="number" min="0" value={lowerSeedStats[player.id]?.threePointersAttempted || 0}
                                    onChange={(e) => updateStat(player.id, false, 'threePointersAttempted', e.target.value)}
                                    className="w-12 px-1 py-1 border rounded text-center" placeholder="A" />
                                </div>
                              </td>
                              <td className="px-2 py-1">
                                <div className="flex gap-1">
                                  <input type="number" min="0" value={lowerSeedStats[player.id]?.freeThrowsMade || 0}
                                    onChange={(e) => updateStat(player.id, false, 'freeThrowsMade', e.target.value)}
                                    className="w-12 px-1 py-1 border rounded text-center" placeholder="M" />
                                  <span>/</span>
                                  <input type="number" min="0" value={lowerSeedStats[player.id]?.freeThrowsAttempted || 0}
                                    onChange={(e) => updateStat(player.id, false, 'freeThrowsAttempted', e.target.value)}
                                    className="w-12 px-1 py-1 border rounded text-center" placeholder="A" />
                                </div>
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={lowerSeedStats[player.id]?.offensiveRebounds || 0}
                                  onChange={(e) => updateStat(player.id, false, 'offensiveRebounds', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <input type="number" min="0" value={lowerSeedStats[player.id]?.fouls || 0}
                                  onChange={(e) => updateStat(player.id, false, 'fouls', e.target.value)}
                                  className="w-16 px-1 py-1 border rounded text-center" />
                              </td>
                              <td className="px-2 py-1">
                                <button
                                  type="button"
                                  onClick={() => removePlayer(player.id, false)}
                                  className="text-red-500 hover:text-red-700 dark:text-red-400 text-lg font-bold"
                                  title={`Remove ${player.firstName} ${player.lastName}`}
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Submitting...' : showStatsEntry ? '💾 Submit Score & Stats' : '💾 Submit Result'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowScoreModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayoffManagement;
